import { useEffect, useState } from "react";
import { Loader2, CalendarPlus } from "lucide-react";
import { toast } from "sonner";

import { runService } from "@/services/runService";
import { EVENT_TYPES } from "@/config/runOptions";
import { Field } from "@/components/profile/FormControls";
import { RelatedEntityPicker } from "@/components/run/RelatedEntityPicker";
import { toLocalInput } from "@/lib/datetime";
import { parseFieldErrors } from "@/lib/format";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const nextHour = () => {
  const d = new Date();
  d.setHours(d.getHours() + 1, 0, 0, 0);
  return toLocalInput(d);
};

/** Create or edit a calendar event. Pass `event` to edit; `presetRelated`/`presetStart` for context. */
export const NewEventDialog = ({ open, onOpenChange, event, presetRelated, presetStart, onSaved }) => {
  const [form, setForm] = useState({ title: "", description: "", event_type: "appointment", start_at: "", end_at: "", all_day: false, location: "" });
  const [related, setRelated] = useState(null);
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const editing = !!event?.id;

  useEffect(() => {
    if (!open) return;
    if (editing) {
      setForm({
        title: event.title || "", description: event.description || "", event_type: event.event_type || "appointment",
        start_at: toLocalInput(event.start_at), end_at: event.end_at ? toLocalInput(event.end_at) : "",
        all_day: !!event.all_day, location: event.location || "",
      });
      setRelated(event.related?.id ? event.related : null);
    } else {
      setForm({ title: "", description: "", event_type: "appointment", start_at: presetStart ? toLocalInput(presetStart) : nextHour(), end_at: "", all_day: false, location: "" });
      setRelated(presetRelated || null);
    }
    setErrors({});
  }, [open, event, editing, presetRelated, presetStart]);

  const save = async () => {
    setErrors({}); setSaving(true);
    try {
      const payload = {
        title: form.title,
        description: form.description || null,
        event_type: form.event_type,
        start_at: form.start_at ? new Date(form.start_at).toISOString() : "",
        end_at: form.end_at ? new Date(form.end_at).toISOString() : null,
        all_day: form.all_day,
        location: form.location || null,
        related_entity_type: related?.type || null,
        related_entity_id: related?.id || null,
      };
      if (editing) await runService.updateEvent(event.id, payload);
      else await runService.createEvent(payload);
      toast.success(editing ? "Event updated" : "Event added");
      onSaved?.();
      onOpenChange(false);
    } catch (err) {
      setErrors(parseFieldErrors(err));
      toast.error(err.message || "Please correct the highlighted fields");
    } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] max-w-lg overflow-y-auto" data-testid="new-event-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><CalendarPlus className="h-5 w-5 text-accent" /> {editing ? "Edit Event" : "New Event"}</DialogTitle>
          <DialogDescription>Appointments, showings, closings, and business events.</DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <Field label="Title" htmlFor="event-title" error={errors.title}>
            <Input id="event-title" value={form.title} onChange={(e) => set("title", e.target.value)} placeholder="e.g. Buyer showing at 42 Oak St" data-testid="event-title" />
          </Field>
          <div className="grid gap-4 sm:grid-cols-2">
            <Field label="Type" htmlFor="event-type">
              <Select value={form.event_type} onValueChange={(v) => set("event_type", v)}>
                <SelectTrigger id="event-type" data-testid="event-type"><SelectValue /></SelectTrigger>
                <SelectContent>{EVENT_TYPES.map((c) => <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>)}</SelectContent>
              </Select>
            </Field>
            <Field label="Location (optional)" htmlFor="event-location">
              <Input id="event-location" value={form.location} onChange={(e) => set("location", e.target.value)} data-testid="event-location" />
            </Field>
          </div>
          <div className="flex items-center gap-3">
            <Checkbox id="event-allday" checked={form.all_day} onCheckedChange={(v) => set("all_day", !!v)} data-testid="event-allday" />
            <label htmlFor="event-allday" className="text-sm">All-day event</label>
          </div>
          <div className="grid gap-4 sm:grid-cols-2">
            <Field label="Starts" htmlFor="event-start" error={errors.start_at}>
              <Input id="event-start" type={form.all_day ? "date" : "datetime-local"} value={form.all_day ? form.start_at.slice(0, 10) : form.start_at} onChange={(e) => set("start_at", form.all_day ? `${e.target.value}T00:00` : e.target.value)} data-testid="event-start" />
            </Field>
            <Field label="Ends (optional)" htmlFor="event-end">
              <Input id="event-end" type={form.all_day ? "date" : "datetime-local"} value={form.all_day ? (form.end_at ? form.end_at.slice(0, 10) : "") : form.end_at} onChange={(e) => set("end_at", form.all_day ? `${e.target.value}T23:59` : e.target.value)} data-testid="event-end" />
            </Field>
          </div>
          <Field label="Link to (optional)"><RelatedEntityPicker value={related} onChange={setRelated} locked={!!presetRelated} /></Field>
          <Field label="Notes (optional)" htmlFor="event-desc">
            <Textarea id="event-desc" rows={2} value={form.description} onChange={(e) => set("description", e.target.value)} data-testid="event-desc" />
          </Field>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="new-event-cancel">Cancel</Button>
          <Button onClick={save} disabled={saving} data-testid="new-event-save">
            {saving ? <><Loader2 className="h-4 w-4 animate-spin" /> Saving…</> : editing ? "Save Changes" : "Add Event"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
